import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";

export default function PrivacyPage() {
  return (
    <Layout>
      <PageHead
        title="Privacy Policy"
        description="Privacy Policy for MOTUS Bug Board, operated by Don Norman Associates."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto py-8">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: May 28, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8 text-slate-700 dark:text-slate-300">

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">1. Who We Are</h2>
            <p>
              MOTUS Bug Board is operated by <strong>Don Norman Associates</strong>. We are an independent consulting firm
              and are not affiliated with the FMCSA. This Privacy Policy describes how we collect, use, and protect
              information when you use MOTUS Bug Board. By using this service, you agree to the practices described here.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">2. Information We Collect</h2>
            <p>We collect the following categories of information:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Account information (staff only):</strong> If you are a Don Norman Associates staff member who
                signs in, your name, email address, and authentication credentials are managed through Clerk (our
                authentication provider). We do not store passwords ourselves.
              </li>
              <li>
                <strong>Bug report content:</strong> When you submit a bug report, we collect your name, email address,
                USDOT number, a description of the issue, and any file attachments you choose to include.
              </li>
              <li>
                <strong>Usage data:</strong> We collect basic analytics about how pages are accessed — including page
                views and general navigation patterns — through Google Analytics 4 (GA4) and PostHog. PostHog also
                captures product interactions (such as clicks) and records anonymized session replays to help us
                understand how the board is used. This data is used in aggregate and does not personally identify you.
              </li>
              <li>
                <strong>Technical data:</strong> Standard server logs may capture IP addresses and browser user-agent
                strings for security and operational purposes. We do not use these for tracking.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Receive, track, and respond to bug reports about the MOTUS system</li>
              <li>Allow submitters to look up their own reports by email</li>
              <li>Allow staff to manage and respond to submitted reports</li>
              <li>Identify patterns in system-wide outages or issues and advise our clients</li>
              <li>Improve the reliability and usability of MOTUS Bug Board</li>
            </ul>
            <p className="mt-2">
              We do not sell your personal information to third parties. We do not use your information for advertising.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">4. Third-Party Services</h2>
            <p>We use the following third-party services to operate MOTUS Bug Board:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Clerk</strong> — handles staff authentication. Your account credentials are governed by{" "}
                <a href="https://clerk.com/privacy" target="_blank" rel="noopener noreferrer" className="dna-link underline">Clerk's Privacy Policy</a>.
              </li>
              <li>
                <strong>Google Analytics 4</strong> — provides aggregated, anonymized page analytics. Data is processed
                in accordance with{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="dna-link underline">Google's Privacy Policy</a>.
              </li>
              <li>
                <strong>PostHog</strong> — provides product analytics and session replay (recordings of page
                interactions) to help us understand how the board is used and improve it. Data is processed in
                accordance with{" "}
                <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="dna-link underline">PostHog's Privacy Policy</a>.
              </li>
              <li>
                <strong>Email provider</strong> — used to send notification emails to staff when new reports are
                submitted. Your email address is used only to send you replies to your submissions.
              </li>
              <li>
                <strong>Object storage</strong> — file attachments you upload are stored securely in cloud object
                storage. They are accessible only to Don Norman Associates staff.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">5. Cookies and Analytics</h2>
            <p>
              MOTUS Bug Board uses cookies and similar technologies for authentication (session cookies managed by Clerk)
              and analytics (Google Analytics 4 and PostHog). GA4 may set cookies to distinguish unique visitors and
              measure page interactions. PostHog uses cookies and similar storage to power product analytics and to
              record anonymized session replays of how the board is used.
            </p>
            <p className="mt-2">
              <strong>To opt out of Google Analytics:</strong> You can install the{" "}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="dna-link underline">
                Google Analytics Opt-out Browser Add-on
              </a>
              , or use your browser's private/incognito mode. Most modern browsers also allow you to block or delete
              cookies in your settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">6. Data Retention</h2>
            <p>
              Bug reports and any associated attachments are retained for as long as they are operationally useful —
              typically the duration of our engagement with affected clients. We periodically review and remove reports
              that are no longer relevant. If you would like your submitted report and personal information removed,
              please contact us (see Section 8).
            </p>
            <p className="mt-2">
              Analytics data retained by Google Analytics follows Google's standard retention settings (default 14
              months). Staff account data retained by Clerk follows Clerk's data retention policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">7. Your Rights</h2>
            <p>
              Depending on your location, you may have the right to access, correct, or delete personal information we
              hold about you, or to object to or restrict certain processing. To exercise any of these rights, please
              contact us using the information in Section 8. We will respond within a reasonable timeframe.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">8. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests related to this Privacy Policy or your personal information,
              please reach out to Don Norman Associates through our company website at{" "}
              <a
                href="https://donnormanassociates.com"
                target="_blank"
                rel="noopener noreferrer"
                className="dna-link underline"
              >
                donnormanassociates.com
              </a>
              .
            </p>
          </section>

        </div>
      </div>
    </Layout>
  );
}
